import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronDown, 
  Smartphone, 
  ChefHat, 
  CreditCard, 
  BarChart3,
  Clock,
  Users,
  Store,
  ArrowRight,
  Check,
  Star,
  Building2,
  UserCircle,
  ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ThemeToggle';

import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function LandingPage() {
  const features = [
    {
      icon: Smartphone,
      title: 'Cloud POS',
      description: 'Modern point of sale that works on any device. Take orders, process payments, and manage your floor.'
    },
    {
      icon: ChefHat,
      title: 'Kitchen Display',
      description: 'Keep your kitchen in perfect sync. Real-time orders, ticket management, and prep tracking.'
    },
    {
      icon: CreditCard,
      title: 'Integrated Payments',
      description: 'Accept all payment types with competitive rates. Cards, mobile pay, and more.'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reports',
      description: 'Real-time insights to grow your business. Sales, labor, inventory all in one place.'
    },
    {
      icon: Clock,
      title: 'Staff Management',
      description: 'Scheduling, time tracking, and payroll. Keep your team organized and efficient.'
    },
    {
      icon: Store,
      title: 'Online Ordering',
      description: 'Your own branded ordering site. Commission-free direct orders from your customers.'
    },
  ];

  const testimonials = [
    {
      quote: "Chain29 transformed how we run our restaurant. The kitchen display alone saved us hours daily.",
      author: "Maria S.",
      role: "Owner, Bella Italia"
    },
    {
      quote: "Finally a POS that just works. Setup was easy and the support team is amazing.",
      author: "James L.",
      role: "Manager, The Burger Joint"
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-white dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Navigation */}
      <header className="sticky top-0 z-50 border-b border-slate-200 dark:border-white/10 bg-white/80 dark:bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-10 w-auto" />
              <span className="text-xl font-bold text-slate-900 dark:text-white">Chain29</span>
            </Link>

            {/* Nav Links - Desktop */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/product" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-sm font-medium">
                Product
              </Link>
              <Link to="/pricing" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-sm font-medium">
                Pricing
              </Link>
              <Link to="/support" className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white text-sm font-medium">
                Support
              </Link>
            </nav>

            {/* Right Side: Theme Toggle + Login Dropdown + CTA */}
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <ThemeToggle className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10" />
              
              {/* Login Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="text-slate-700 dark:text-white hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/10"
                    data-testid="login-dropdown-trigger"
                  >
                    Login
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/owner/login" className="flex items-center gap-2 cursor-pointer" data-testid="login-owner">
                      <Building2 className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Restaurant Owner</div>
                        <div className="text-xs text-muted-foreground">Manage your restaurant</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/staff/login" className="flex items-center gap-2 cursor-pointer" data-testid="login-staff">
                      <UserCircle className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Employee / Staff</div>
                        <div className="text-xs text-muted-foreground">Clock in & access POS</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/auth" className="flex items-center gap-2 cursor-pointer" data-testid="login-customer">
                      <ShoppingBag className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Customer</div>
                        <div className="text-xs text-muted-foreground">Order food online</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Primary CTA */}
              <Link to="/owner/signup">
                <Button 
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="start-restaurant-btn"
                >
                  Start Your Restaurant
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
            Run Your Restaurant<br />
            <span className="text-orange-500 dark:text-orange-400">with Chain29</span>
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
            POS • Kitchen Display • Payments • Reporting<br />
            Everything you need to grow your restaurant business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/owner/signup">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                Start Your Restaurant
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500 dark:text-green-400" />
              7-day free trial
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500 dark:text-green-400" />
              No credit card required
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-4 h-4 text-green-500 dark:text-green-400" />
              Cancel anytime
            </span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-100 dark:bg-black/20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-4">
            Everything Your Restaurant Needs
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            From taking orders to managing your team, Chain29 has you covered.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="p-6 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 hover:border-orange-500/50 dark:hover:border-orange-500/30 transition-colors shadow-sm"
              >
                <div className="p-3 rounded-lg bg-orange-100 dark:bg-orange-500/20 w-fit mb-4">
                  <feature.icon className="w-6 h-6 text-orange-500 dark:text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-6 h-6 fill-orange-400 text-orange-400" />
              ))}
            </div>
            <p className="text-slate-600 dark:text-slate-400">Trusted by 450+ restaurants</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm">
                <p className="text-slate-700 dark:text-white mb-4 italic">"{t.quote}"</p>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">{t.author}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Restaurant?
          </h2>
          <p className="text-orange-100 mb-8 max-w-xl mx-auto">
            Join hundreds of restaurants already using Chain29 to grow their business.
          </p>
          <Link to="/owner/signup">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-orange-50 px-8">
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-200 dark:border-white/10 bg-white dark:bg-transparent">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-6 w-6" />
              <span className="text-slate-900 dark:text-white font-semibold">Chain29</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500 dark:text-slate-400">
              <Link to="/product" className="hover:text-slate-900 dark:hover:text-white">Product</Link>
              <Link to="/pricing" className="hover:text-slate-900 dark:hover:text-white">Pricing</Link>
              <Link to="/about" className="hover:text-slate-900 dark:hover:text-white">About</Link>
              <Link to="/contact" className="hover:text-slate-900 dark:hover:text-white">Contact</Link>
              <Link to="/terms" className="hover:text-slate-900 dark:hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-slate-900 dark:hover:text-white">Privacy</Link>
            </div>
            <p className="text-sm text-slate-400 dark:text-slate-500">© 2026 Chain29. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
